import { useEffect, useState } from "react";
import { api, fmtDate, fmtINR, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Paperclip, Info, Trash } from "@phosphor-icons/react";
import { Field } from "./Employees";
import ApprovalActionDialog from "@/components/ApprovalActionDialog";
import WorkflowToolbar from "@/components/WorkflowToolbar";
import Pagination from "@/components/Pagination";
import BulkPaidBar from "@/components/BulkPaidBar";
import { EXPENSE_TYPES } from "@/lib/constants";
import { StatusPill } from "./Travel";

const CATEGORIES = [
  { value: "travel_settlement", label: "Travel Settlement" },
  { value: "travel_allowance", label: "Travel Allowance" },
  { value: "phone_bill", label: "Phone Bill" },
  { value: "office_expense", label: "Office Expenses" },
];

// For travel allowance, hide flight/hotel/food/phone_bill etc.
const TRAVEL_ALLOWANCE_TYPES = EXPENSE_TYPES.filter((t) => ["train","bus","auto","metro","e_rickshaw","bike","taxi","fuel","other"].includes(t.value));
const OFFICE_TYPES = EXPENSE_TYPES.filter((t) => t.value !== "phone_bill");

const newLine = (kind = "office") => ({
  expense_type: kind === "travel" ? "auto" : "food",
  custom_type: "",
  amount: 0,
  description: "",
  attachment_ids: [],
  from_location: "",
  to_location: "",
});

export default function Expenses() {
  const { user } = useAuth();
  const canApply = user?.role !== "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const [paidSelected, setPaidSelected] = useState(new Set());
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [approvedTravels, setApprovedTravels] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const empty = {
    category: "office_expense", travel_id: null,
    expense_date: new Date().toISOString().slice(0, 10),
    description: "",
    lines: [newLine("office")],
    // phone_bill fields
    phone_number: "", service_provider: "", recharge_date: "",
    amount: 0, attachment_ids: [],
    auto_pay_on_approval: false,
  };
  const [form, setForm] = useState(empty);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [uploadingPhone, setUploadingPhone] = useState(false);
  const [reviewId, setReviewId] = useState(null);
  const [filters, setFilters] = useState({ page: 1, page_size: 25 });

  const load = () => {
    const params = { ...filters };
    Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
    return api.get("/expenses", { params }).then((r) => {
      if (Array.isArray(r.data)) { setItems(r.data); setTotalCount(r.data.length); }
      else { setItems(r.data.items || []); setTotalCount(r.data.total || 0); }
    });
  };
  const loadSummary = () => {
    // Summary respects visibility scope + all filters except status/tab
    const { status, ...rest } = filters;  // eslint-disable-line no-unused-vars
    const params = { ...rest };
    Object.keys(params).forEach((k) => { if (!params[k] || k === "page" || k === "page_size") delete params[k]; });
    api.get("/expenses/summary", { params }).then((r) => setSummary(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadSummary(); }, [filters]);
  useEffect(() => {
    api.get("/travel").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setApprovedTravels(list.filter((t) => t.status === "approved"));
    });
  }, []);

  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("category", "expense");
    const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.id;
  };
  const uploadOnLine = async (idx, files) => {
    if (!files || files.length === 0) return;
    setUploadingIdx(idx);
    try {
      const ids = [];
      for (const f of files) ids.push(await uploadFile(f));
      const arr = [...form.lines];
      arr[idx] = { ...arr[idx], attachment_ids: [...(arr[idx].attachment_ids || []), ...ids] };
      setForm({ ...form, lines: arr });
      toast.success(`${ids.length} file(s) attached`);
    } catch { toast.error("Upload failed"); } finally { setUploadingIdx(null); }
  };
  const removeAttachment = (idx, fileId) => {
    const arr = [...form.lines];
    arr[idx] = { ...arr[idx], attachment_ids: (arr[idx].attachment_ids || []).filter((x) => x !== fileId) };
    setForm({ ...form, lines: arr });
  };
  const uploadPhone = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingPhone(true);
    try {
      const ids = [];
      for (const f of files) ids.push(await uploadFile(f));
      setForm({ ...form, attachment_ids: [...(form.attachment_ids || []), ...ids] });
      toast.success(`${ids.length} file(s) attached`);
    } catch { toast.error("Upload failed"); } finally { setUploadingPhone(false); }
  };
  const removePhoneAtt = (fid) => setForm({ ...form, attachment_ids: (form.attachment_ids || []).filter((x) => x !== fid) });

  const updateLine = (idx, key, val) => {
    const arr = [...form.lines]; arr[idx] = { ...arr[idx], [key]: val }; setForm({ ...form, lines: arr });
  };
  const addLine = () => setForm({ ...form, lines: [...form.lines, newLine(form.category === "travel_allowance" || form.category === "travel_settlement" ? "travel" : "office")] });
  const removeLine = (idx) => {
    if (form.lines.length === 1) return toast.error("At least one line is required");
    const arr = [...form.lines]; arr.splice(idx, 1); setForm({ ...form, lines: arr });
  };
  const onCategoryChange = (v) => {
    // seed sensible defaults per category
    if (v === "phone_bill") {
      setForm({ ...form, category: v, travel_id: null, lines: [] });
    } else if (v === "travel_allowance" || v === "travel_settlement") {
      setForm({ ...form, category: v, lines: [newLine("travel")] });
    } else {
      setForm({ ...form, category: v, travel_id: null, lines: [newLine("office")] });
    }
  };

  const total = form.category === "phone_bill"
    ? (Number(form.amount) || 0)
    : form.lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  const save = async () => {
    if (!form.expense_date) return toast.error("Date required");
    let body;
    if (form.category === "phone_bill") {
      if (!form.phone_number?.trim()) return toast.error("Phone number is required");
      if (!form.service_provider?.trim()) return toast.error("Service provider is required");
      if (!form.recharge_date) return toast.error("Recharge date is required");
      if (!Number(form.amount)) return toast.error("Amount is required");
      if (!(form.attachment_ids || []).length) return toast.error("Please attach the bill / invoice");
      body = {
        category: "phone_bill",
        expense_date: form.expense_date,
        description: form.description,
        expense_type: "phone_bill",
        amount: Number(form.amount),
        attachment_ids: form.attachment_ids,
        phone_number: form.phone_number,
        service_provider: form.service_provider,
        recharge_date: form.recharge_date,
        auto_pay_on_approval: !!form.auto_pay_on_approval,
      };
    } else {
      if (form.lines.some((l) => !Number(l.amount))) return toast.error("Each line needs an amount");
      if (form.category === "travel_settlement" && !form.travel_id) return toast.error("Select an approved travel for Travel Settlement");
      body = {
        category: form.category, travel_id: form.travel_id,
        expense_date: form.expense_date, description: form.description,
        lines: form.lines.map((l) => ({ ...l, amount: Number(l.amount) })),
        auto_pay_on_approval: !!form.auto_pay_on_approval,
      };
    }
    try {
      await api.post("/expenses", body);
      toast.success("Submitted"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const cancel = async (e) => {
    if (!window.confirm(`Cancel this expense claim of ${fmtINR(e.amount)}?`)) return;
    try { await api.post(`/expenses/${e.id}/cancel`); toast.success("Cancelled"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const filtered = items;  // Backend already filtered by status via filters.status
  const counts = summary ? {
    all: summary.total_claims || 0,
    pending: (summary.pending || 0) + (summary.info_requested || 0),
    approved: summary.approved || 0,
    rejected: summary.rejected || 0,
    cancelled: summary.cancelled || 0,
    paid: summary.paid || 0,
  } : { all: totalCount, pending: 0, approved: 0, rejected: 0, cancelled: 0, paid: 0 };

  const onTabChange = (v) => {
    setTab(v);
    const map = { all: "", pending: "pending", approved: "approved", rejected: "rejected", cancelled: "cancelled", paid: "paid" };
    setFilters((f) => ({ ...f, status: map[v] || "", page: 1 }));
  };
  const isTravelCat = form.category === "travel_allowance" || form.category === "travel_settlement";
  const typeOptions = isTravelCat ? TRAVEL_ALLOWANCE_TYPES : OFFICE_TYPES;

  return (
    <div data-testid="expenses-page">
      <PageHeader title="Expense Claims" description={canApply ? "Submit reimbursements & track approvals." : "Review and approve expense claims."} action={canApply && (
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(empty); }}>
          <DialogTrigger asChild><Button onClick={() => setForm(empty)} className="btn-brand" data-testid="expense-new"><Plus size={16} className="mr-1.5" />New Claim</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Expense Claim</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Category *">
                <Select value={form.category} onValueChange={onCategoryChange}>
                  <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Date *"><Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} /></Field>
              {form.category === "travel_settlement" && (
                <div className="sm:col-span-2"><Field label="Linked Approved Travel *">
                  <Select value={form.travel_id || ""} onValueChange={(v) => setForm({ ...form, travel_id: v })}>
                    <SelectTrigger data-testid="expense-travel"><SelectValue placeholder="Pick an approved travel" /></SelectTrigger>
                    <SelectContent>
                      {approvedTravels.length === 0 && <div className="px-3 py-2 text-xs opacity-60">No approved travels.</div>}
                      {approvedTravels.map((t) => <SelectItem key={t.id} value={t.id}>{t.purpose} — {t.from_location} → {t.to_location}</SelectItem>)}
                    </SelectContent>
                  </Select></Field></div>
              )}
              <div className="sm:col-span-2">
                <Field label="Description (overall)"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional notes…" rows={2} /></Field>
              </div>
            </div>

            {/* PHONE BILL SPECIFIC FORM */}
            {form.category === "phone_bill" && (
              <div className="mt-5 space-y-3 border rounded-md p-4" style={{ borderColor: "hsl(var(--border))" }}>
                <h4 className="font-semibold text-sm">Phone Bill Reimbursement</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Phone Number *"><Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} data-testid="phone-number" /></Field>
                  <Field label="Service Provider *"><Input value={form.service_provider} onChange={(e) => setForm({ ...form, service_provider: e.target.value })} placeholder="e.g. Airtel / Jio / Vi / BSNL" data-testid="phone-provider" /></Field>
                  <Field label="Recharge Date *"><Input type="date" value={form.recharge_date} onChange={(e) => setForm({ ...form, recharge_date: e.target.value })} data-testid="phone-recharge-date" /></Field>
                  <Field label="Amount (₹) *"><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="phone-amount" /></Field>
                  <div className="sm:col-span-2">
                    <Field label="Bill / Invoice attachment(s) *">
                      <Input type="file" multiple onChange={(e) => uploadPhone(Array.from(e.target.files || []))} data-testid="phone-file" />
                      {uploadingPhone && <span className="text-xs opacity-60">Uploading…</span>}
                      {(form.attachment_ids || []).length > 0 && (
                        <ul className="mt-2 flex flex-wrap gap-2 text-xs">
                          {form.attachment_ids.map((fid) => (
                            <li key={fid} className="inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                              <Paperclip size={12} className="text-[#fd7e14]" />
                              <a href={fileUrl(fid)} target="_blank" rel="noreferrer" className="text-[#fd7e14] underline">{fid.slice(0, 6)}…</a>
                              <button type="button" onClick={() => removePhoneAtt(fid)} className="text-red-600 ml-1">×</button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </Field>
                  </div>
                </div>
              </div>
            )}

            {/* MULTI-LINE FOR OTHER CATEGORIES */}
            {form.category !== "phone_bill" && (
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">{isTravelCat ? "Travel items" : "Expense items"} ({form.lines.length})</h4>
                  <Button size="sm" variant="outline" onClick={addLine} data-testid="expense-add-line"><Plus size={14} className="mr-1" /> Add item</Button>
                </div>
                <div className="space-y-3">
                  {form.lines.map((ln, idx) => (
                    <div key={idx} className="border rounded-md p-3" style={{ borderColor: "hsl(var(--border))" }} data-testid={`expense-line-${idx}`}>
                      <div className="grid sm:grid-cols-12 gap-2 items-end">
                        <div className="sm:col-span-3">
                          <label className="text-xs">Type *</label>
                          <Select value={ln.expense_type} onValueChange={(v) => updateLine(idx, "expense_type", v)}>
                            <SelectTrigger className="mt-1" data-testid={`expense-line-${idx}-type`}><SelectValue /></SelectTrigger>
                            <SelectContent>{typeOptions.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                          </Select>
                          {ln.expense_type === "other" && (
                            <Input className="mt-2" placeholder="Specify type…" value={ln.custom_type || ""} onChange={(e) => updateLine(idx, "custom_type", e.target.value)} data-testid={`expense-line-${idx}-custom`} />
                          )}
                        </div>
                        <div className="sm:col-span-2">
                          <label className="text-xs">Amount (₹) *</label>
                          <Input type="number" className="mt-1" value={ln.amount} onChange={(e) => updateLine(idx, "amount", e.target.value)} data-testid={`expense-line-${idx}-amount`} />
                        </div>
                        <div className="sm:col-span-6">
                          <label className="text-xs">Description</label>
                          <Input className="mt-1" value={ln.description || ""} onChange={(e) => updateLine(idx, "description", e.target.value)} placeholder="Brief note for this item" />
                        </div>
                        <div className="sm:col-span-1 flex sm:justify-end">
                          <Button size="icon" variant="ghost" onClick={() => removeLine(idx)} className="text-red-600"><Trash size={16} /></Button>
                        </div>
                      </div>
                      {isTravelCat && (
                        <div className="grid sm:grid-cols-2 gap-2 mt-2">
                          <div><label className="text-xs">From *</label><Input className="mt-1" value={ln.from_location || ""} onChange={(e) => updateLine(idx, "from_location", e.target.value)} placeholder="e.g. Salt Lake" data-testid={`expense-line-${idx}-from`} /></div>
                          <div><label className="text-xs">To *</label><Input className="mt-1" value={ln.to_location || ""} onChange={(e) => updateLine(idx, "to_location", e.target.value)} placeholder="e.g. Park Street" data-testid={`expense-line-${idx}-to`} /></div>
                        </div>
                      )}
                      <div className="mt-3">
                        <label className="text-xs">Attachments (multiple files supported)</label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input type="file" multiple onChange={(e) => uploadOnLine(idx, Array.from(e.target.files || []))} data-testid={`expense-line-${idx}-files`} />
                          {uploadingIdx === idx && <span className="text-xs opacity-60">Uploading…</span>}
                        </div>
                        {(ln.attachment_ids || []).length > 0 && (
                          <ul className="mt-2 flex flex-wrap gap-2 text-xs">
                            {ln.attachment_ids.map((fid) => (
                              <li key={fid} className="inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                                <Paperclip size={12} className="text-[#fd7e14]" />
                                <a href={fileUrl(fid)} target="_blank" rel="noreferrer" className="text-[#fd7e14] underline">{fid.slice(0, 6)}…</a>
                                <button type="button" onClick={() => removeAttachment(idx, fid)} className="text-red-600 ml-1">×</button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-end gap-4 text-sm border-t pt-3" style={{ borderColor: "hsl(var(--border))" }}>
                  <span className="opacity-70">Total items: {form.lines.length}</span>
                  <span>Grand Total: <b className="text-[#fd7e14] text-lg">{fmtINR(total)}</b></span>
                </div>
              </div>
            )}

            <div className="mt-4 border-t pt-3" style={{ borderColor: "hsl(var(--border))" }}>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="expense-autopay-label">
                <input
                  type="checkbox"
                  className="rounded border-slate-400 h-4 w-4 accent-[#fd7e14]"
                  checked={!!form.auto_pay_on_approval}
                  onChange={(e) => setForm({ ...form, auto_pay_on_approval: e.target.checked })}
                  data-testid="expense-autopay"
                />
                <span>I have already paid this amount — <b>auto mark as Paid once Finance Manager approves</b></span>
              </label>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="btn-brand" data-testid="expense-save">Submit Claim</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )} />

      {!canApply && (<div className="mb-4 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/40 p-3 text-sm">
        <Info size={18} className="text-amber-700 dark:text-amber-300 shrink-0 mt-0.5" />
        <div><div className="font-medium text-amber-900 dark:text-amber-200">Super Admin view</div>
        <div className="text-amber-800 dark:text-amber-300/90">Super Admins cannot file expense claims — only review and approve.</div></div></div>)}

      <WorkflowToolbar kind="expense" filters={filters} setFilters={setFilters}
        extraTypeOptions={[
          { value: "flight", label: "Flight" }, { value: "train", label: "Train" }, { value: "bus", label: "Bus" },
          { value: "auto", label: "Auto" }, { value: "metro", label: "Metro" }, { value: "e_rickshaw", label: "E-Rickshaw" },
          { value: "bike", label: "Bike" }, { value: "hotel", label: "Hotel" }, { value: "taxi", label: "Taxi" },
          { value: "fuel", label: "Fuel" }, { value: "food", label: "Food" }, { value: "phone_bill", label: "Phone Bill" },
        ]} />

      <Tabs value={tab} onValueChange={onTabChange}>
        <TabsList>
          <TabsTrigger value="all" data-testid="exp-tab-all">All ({counts.all})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="exp-tab-pending">Pending ({counts.pending})</TabsTrigger>
          <TabsTrigger value="approved" data-testid="exp-tab-approved">Approved ({counts.approved})</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="exp-tab-rejected">Declined ({counts.rejected})</TabsTrigger>
          <TabsTrigger value="paid" data-testid="exp-tab-paid">Paid ({counts.paid})</TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="exp-tab-cancelled">Cancelled ({counts.cancelled})</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          {isSuperAdmin && (
            <BulkPaidBar kind="expense" rows={filtered} selected={paidSelected} setSelected={setPaidSelected}
              onDone={() => { load(); loadSummary(); }} />
          )}
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                {isSuperAdmin && <TableHead className="w-10"></TableHead>}
                <TableHead>Category</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Date</TableHead>
                <TableHead>Attachments</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((e) => {
                  const lines = e.lines && e.lines.length ? e.lines : [{ expense_type: e.expense_type, amount: e.amount, description: e.description, attachment_ids: e.attachment_id ? [e.attachment_id] : (e.attachment_ids || []) }];
                  const allAtts = e.category === "phone_bill" ? (e.attachment_ids || (e.attachment_id ? [e.attachment_id] : [])) : lines.flatMap((l) => l.attachment_ids || []);
                  const canCancel = (e.applicant_id === user.id || user.role === "super_admin") && (e.status === "pending" || e.status === "info_requested");
                  const canSelectPaid = isSuperAdmin && e.status === "approved" && e.approval_id;
                  return (
                    <TableRow key={e.id}>
                      {isSuperAdmin && (
                        <TableCell>
                          {canSelectPaid && (
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-[#fd7e14]"
                              checked={paidSelected.has(e.approval_id)}
                              onChange={() => {
                                const s = new Set(paidSelected);
                                s.has(e.approval_id) ? s.delete(e.approval_id) : s.add(e.approval_id);
                                setPaidSelected(s);
                              }}
                              data-testid={`expense-paid-check-${e.id}`}
                            />
                          )}
                        </TableCell>
                      )}
                      <TableCell className="capitalize">{(e.category || "office_expense").replace(/_/g, " ")}</TableCell>
                      <TableCell className="text-xs">
                        {e.category === "phone_bill" ? (
                          <div>
                            <div>{e.phone_number} • {e.service_provider}</div>
                            {e.recharge_date && <div className="opacity-60">Recharged {fmtDate(e.recharge_date)}</div>}
                          </div>
                        ) : lines.map((l, i) => (
                          <div key={i} className="capitalize">
                            {(l.expense_type || "").replace(/_/g, " ")}{l.custom_type ? ` (${l.custom_type})` : ""} • {fmtINR(l.amount)}
                            {l.from_location && l.to_location && <span className="opacity-60"> — {l.from_location} → {l.to_location}</span>}
                          </div>
                        ))}
                      </TableCell>
                      <TableCell className="font-semibold">{fmtINR(e.amount)}</TableCell>
                      <TableCell className="text-sm">{fmtDate(e.expense_date)}</TableCell>
                      <TableCell className="text-xs space-x-1.5">
                        {allAtts.length === 0 ? "—" : allAtts.map((fid, i) => (
                          <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" className="text-[#fd7e14] underline">View{allAtts.length > 1 ? " " + (i + 1) : ""}</a>
                        ))}
                      </TableCell>
                      <TableCell><StatusPill status={e.status} /></TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => setReviewId(e.id)} data-testid={`expense-review-${e.id}`}>Review</Button>
                        {canCancel && <Button size="sm" variant="outline" className="text-red-600" onClick={() => cancel(e)} data-testid={`expense-cancel-${e.id}`}>Cancel</Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && <TableRow><TableCell colSpan={isSuperAdmin ? 8 : 7} className="text-center text-sm opacity-60 py-10">No expenses.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
          <Pagination total={totalCount} filters={filters} setFilters={setFilters} />
        </TabsContent>
      </Tabs>

      <ApprovalActionDialog
        open={!!reviewId}
        onOpenChange={(o) => !o && setReviewId(null)}
        reqType="expense"
        reqId={reviewId}
        onDone={load}
      />
    </div>
  );
}
